import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Clock3,
  GitBranch,
  Link2,
  MessageSquare,
  Play,
  Plus,
  Save,
  ShieldCheck,
  Square,
  Trash2,
  Webhook,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { Modal } from '@/components/common/Modal'
import {
  createId,
  DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE,
  DEFAULT_FOLLOWUP_WEBHOOK_CONFIG,
  DEFAULT_FOLLOWUP_WEBHOOK_URL,
  type AgentDefinition,
  type AgentFollowUp,
  type AgentKnowledgeSource,
  type AgentSubAgent,
  type ConversationStatusFilter,
  type FollowUpFlowStatus,
  type FollowUpRuleConfig,
  type FollowUpWebhookConfig,
  type LastMessageDirection,
  useAgentCatalogStore,
} from '@/stores/agentCatalogStore'
import { useInstancesStore } from '@/stores/instancesStore'
import AgentsService from '@/services/agents.service'

const DEFAULT_RULE_CONFIG: FollowUpRuleConfig = {
  leadWithoutResponseForMinutes: 10,
  lastMessageDirection: 'inbound',
  conversationStatus: 'open',
  contactLimit: 100,
  onlyBusinessHours: false,
  businessHoursStart: '08:00',
  businessHoursEnd: '18:00',
}

const FLOW_STATUS_OPTIONS: Array<{
  label: string
  value: FollowUpFlowStatus
  icon: React.ReactNode
}> = [
  { label: 'Ativar', value: 'active', icon: <Play size={14} /> },
  { label: 'Pausar', value: 'paused', icon: <Clock3 size={14} /> },
  { label: 'Desativar', value: 'disabled', icon: <Square size={14} /> },
]

function normalizeRuleConfig(
  raw: Partial<FollowUpRuleConfig> | null | undefined,
): FollowUpRuleConfig {
  return {
    leadWithoutResponseForMinutes:
      Number(raw?.leadWithoutResponseForMinutes) > 0
        ? Number(raw?.leadWithoutResponseForMinutes)
        : DEFAULT_RULE_CONFIG.leadWithoutResponseForMinutes,
    lastMessageDirection:
      raw?.lastMessageDirection === 'inbound' ||
      raw?.lastMessageDirection === 'outbound' ||
      raw?.lastMessageDirection === 'any'
        ? raw.lastMessageDirection
        : DEFAULT_RULE_CONFIG.lastMessageDirection,
    conversationStatus:
      raw?.conversationStatus === 'open' ||
      raw?.conversationStatus === 'waiting' ||
      raw?.conversationStatus === 'any'
        ? raw.conversationStatus
        : DEFAULT_RULE_CONFIG.conversationStatus,
    contactLimit:
      Number(raw?.contactLimit) > 0 ? Number(raw?.contactLimit) : DEFAULT_RULE_CONFIG.contactLimit,
    onlyBusinessHours: Boolean(raw?.onlyBusinessHours),
    businessHoursStart:
      typeof raw?.businessHoursStart === 'string' && raw.businessHoursStart.trim()
        ? raw.businessHoursStart
        : DEFAULT_RULE_CONFIG.businessHoursStart,
    businessHoursEnd:
      typeof raw?.businessHoursEnd === 'string' && raw.businessHoursEnd.trim()
        ? raw.businessHoursEnd
        : DEFAULT_RULE_CONFIG.businessHoursEnd,
  }
}

function getFlowStatus(flow: AgentDefinition): FollowUpFlowStatus {
  const raw = flow.metadata?.follow_up_status
  if (raw === 'active' || raw === 'paused' || raw === 'disabled') return raw
  return flow.enabled ? 'active' : 'paused'
}

function getFlowRuleConfig(flow: AgentDefinition): FollowUpRuleConfig {
  return normalizeRuleConfig(flow.metadata?.follow_up_rule_config)
}

function normalizeWebhookConfig(
  raw: Partial<FollowUpWebhookConfig> | null | undefined,
): FollowUpWebhookConfig {
  const legacy = raw as { url?: string; body_template?: string } | null | undefined
  return {
    enabled: raw?.enabled ?? DEFAULT_FOLLOWUP_WEBHOOK_CONFIG.enabled,
    endpoint:
      typeof raw?.endpoint === 'string' && raw.endpoint.trim()
        ? raw.endpoint
        : typeof legacy?.url === 'string' && legacy.url.trim()
          ? legacy.url
          : DEFAULT_FOLLOWUP_WEBHOOK_URL,
    method: 'POST',
    bodyTemplate:
      typeof raw?.bodyTemplate === 'string' && raw.bodyTemplate.trim()
        ? raw.bodyTemplate
        : typeof legacy?.body_template === 'string' && legacy.body_template.trim()
          ? legacy.body_template
          : DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE,
    headers:
      typeof raw?.headers === 'string'
        ? raw.headers
        : raw?.headers && typeof raw.headers === 'object' && !Array.isArray(raw.headers)
          ? Object.entries(raw.headers as Record<string, unknown>)
              .map(([key, value]) => `${key}: ${String(value ?? '')}`)
              .join('\n')
          : '',
    failStrategy: raw?.failStrategy === 'stop' ? 'stop' : 'continue',
  }
}

function getFlowWebhookConfig(flow: AgentDefinition): FollowUpWebhookConfig {
  return normalizeWebhookConfig(flow.metadata?.follow_up_webhook_config)
}

function getFlowInstanceIds(flow: AgentDefinition) {
  const raw = flow.metadata?.follow_up_instance_ids
  if (!Array.isArray(raw)) return []
  return raw
    .map((value) => String(value ?? '').trim())
    .filter((value, index, list) => value && list.indexOf(value) === index)
}

const FOLLOWUP_WEBHOOK_VARIABLES: Array<{
  key: string
  description: string
}> = [
  { key: 'telefone', description: 'Telefone do lead' },
  {
    key: 'mensagem',
    description:
      'Ultima mensagem recebida do lead. Nao representa o texto que sera enviado.',
  },
  { key: 'nome', description: 'Nome do lead' },
  { key: 'conversationId', description: 'ID da conversa' },
  { key: 'contactId', description: 'ID do contato' },
  { key: 'lastInboundMessageAt', description: 'Quando a ultima mensagem do lead chegou' },
  { key: 'followupMessage', description: 'Mensagem configurada na etapa do follow-up' },
]

function createFollowUp(seed = 1): AgentFollowUp {
  return {
    id: createId(),
    title: `Follow-up ${seed}`,
    delay_minutes: seed === 1 ? 10 : seed * 30,
    instructions: '',
    active: true,
  }
}

function createFlow(seed = 1): AgentDefinition {
  const now = new Date().toISOString()
  return {
    id: createId(),
    name: `Fluxo de Follow-up ${seed}`,
    supervisor_name: `follow_up_${seed}`,
    persona_name: 'Follow-up',
    personality: 'Consultivo',
    system_prompt: '',
    knowledge_base: '',
    knowledge_sources: [],
    enabled: false,
    memory_enabled: true,
    strict_logic_flow: true,
    followups: [createFollowUp()],
    subagents: [],
    metadata: {
      follow_up_status: 'paused',
      follow_up_rule_config: DEFAULT_RULE_CONFIG,
      follow_up_webhook_config: DEFAULT_FOLLOWUP_WEBHOOK_CONFIG,
      follow_up_instance_ids: [],
    },
    created_at: now,
    updated_at: now,
  }
}

function getFirstDelayLabel(followups: AgentFollowUp[]) {
  if (followups.length === 0) return 'Sem etapas'
  const ordered = [...followups].sort((left, right) => left.delay_minutes - right.delay_minutes)
  return `${ordered[0].delay_minutes} min`
}

function getStatusBadgeVariant(status: FollowUpFlowStatus): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success'
  if (status === 'paused') return 'warning'
  return 'default'
}

function getStatusLabel(status: FollowUpFlowStatus) {
  if (status === 'active') return 'Ativo'
  if (status === 'paused') return 'Pausado'
  return 'Desativado'
}

function getLastMessageDirectionLabel(value: LastMessageDirection) {
  if (value === 'outbound') return 'Enviada pelo agente/sistema'
  if (value === 'inbound') return 'Recebida do lead'
  return 'Qualquer uma'
}

function getConversationStatusLabel(value: ConversationStatusFilter) {
  if (value === 'open') return 'Aberta'
  if (value === 'waiting') return 'Aguardando'
  return 'Qualquer status'
}

function buildRuleSummary(
  ruleConfig: FollowUpRuleConfig,
  webhookConfig?: FollowUpWebhookConfig,
) {
  const businessHoursClause = ruleConfig.onlyBusinessHours
    ? `AND dentro do horário ${ruleConfig.businessHoursStart} às ${ruleConfig.businessHoursEnd}`
    : ''

  const webhookClause = webhookConfig?.enabled && webhookConfig.endpoint
    ? `\nTHEN enviar webhook para ${webhookConfig.endpoint} com:
{
  "telefone": "{{telefone}}",
  "mensagem": "última mensagem recebida do lead"
}`
    : '\nTHEN disparar follow-up'

  return `IF lead não responder por ${ruleConfig.leadWithoutResponseForMinutes} minutos
AND última mensagem for ${getLastMessageDirectionLabel(ruleConfig.lastMessageDirection)}
AND conversa estiver ${getConversationStatusLabel(ruleConfig.conversationStatus)}
AND contato estiver dentro do limite ${ruleConfig.contactLimit}
${businessHoursClause}${webhookClause}
ELSE não disparar`.trim()
}

function sanitizeKnowledgeSources(sources: AgentKnowledgeSource[]) {
  return sources.map((source) => ({
    id: source.id,
    type: source.type,
    title: source.title,
    content: source.content,
    url: source.url,
    file_name: source.file_name,
    mime_type: source.mime_type,
    size_bytes: source.size_bytes,
    data_url: source.data_url,
  }))
}

function sanitizeSubagents(subagents: AgentSubAgent[]) {
  return subagents.map((subagent) => ({
    ...subagent,
    skills: subagent.skills.map((skill) => ({
      ...skill,
      request_body_fields: skill.request_body_fields ?? [],
      reference_source: skill.reference_source ?? null,
    })),
  }))
}

function parseHeadersText(headersText: string) {
  return headersText.split('\n').reduce<Record<string, string>>((acc, line) => {
    if (!line.includes(':')) return acc
    const [key, ...rest] = line.split(':')
    const normalizedKey = key.trim()
    if (!normalizedKey) return acc
    acc[normalizedKey] = rest.join(':').trim()
    return acc
  }, {})
}

function validateWebhookConfig(webhookConfig: FollowUpWebhookConfig) {
  if (!webhookConfig.enabled) return

  if (!webhookConfig.endpoint.trim()) {
    throw new Error('Endpoint do webhook é obrigatório.')
  }

  try {
    new URL(webhookConfig.endpoint)
  } catch {
    throw new Error('Endpoint do webhook inválido.')
  }

  const rendered = webhookConfig.bodyTemplate
    .replaceAll('{{telefone}}', '5548999999999')
    .replaceAll('{{mensagem}}', 'Mensagem de teste')

  try {
    JSON.parse(rendered)
  } catch {
    throw new Error('Body do webhook precisa ser um JSON válido.')
  }
}

function validateFollowUpFlow(flow: AgentDefinition, ruleConfig: FollowUpRuleConfig) {
  if (!flow.name.trim()) {
    throw new Error('Nome do fluxo é obrigatório.')
  }

  const status = getFlowStatus(flow)
  if (!['active', 'paused', 'disabled'].includes(status)) {
    throw new Error('Status do fluxo inválido.')
  }

  if (!ruleConfig.leadWithoutResponseForMinutes || ruleConfig.leadWithoutResponseForMinutes < 1) {
    throw new Error('Tempo sem resposta precisa ser maior que zero.')
  }

  if (!ruleConfig.contactLimit || ruleConfig.contactLimit < 1) {
    throw new Error('Limite de contatos precisa ser maior que zero.')
  }

  if (flow.followups.length === 0) {
    throw new Error('Adicione pelo menos uma etapa de follow-up.')
  }
}

function FollowUpSequenceEditor({
  followups,
  onChange,
}: {
  followups: AgentFollowUp[]
  onChange: (followups: AgentFollowUp[]) => void
}) {
  function updateFollowUp(id: string, updates: Partial<AgentFollowUp>) {
    onChange(followups.map((followup) => (followup.id === id ? { ...followup, ...updates } : followup)))
  }

  function removeFollowUp(id: string) {
    onChange(followups.filter((followup) => followup.id !== id))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Etapas do follow-up</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Cada etapa guarda atraso, mensagem e ativação individual.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          icon={<Plus size={14} />}
          onClick={() => onChange([...followups, createFollowUp(followups.length + 1)])}
        >
          Nova etapa
        </Button>
      </div>

      {followups.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-5 text-sm text-[var(--text-muted)]">
          Nenhuma etapa configurada ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {followups.map((followup, index) => (
            <div
              key={followup.id}
              className="rounded-[22px] border border-[var(--border-subtle)] bg-[var(--glass-medium)] p-4 shadow-[var(--shadow-sm)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {followup.title || `Follow-up ${index + 1}`}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    Disparo após {followup.delay_minutes} min sem resposta.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateFollowUp(followup.id, { active: !followup.active })}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      followup.active
                        ? 'bg-[rgba(16,185,129,0.12)] text-[var(--color-success)]'
                        : 'bg-[rgba(148,163,184,0.12)] text-[var(--text-muted)]'
                    }`}
                  >
                    {followup.active ? 'Ativa' : 'Pausada'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFollowUp(followup.id)}
                    className="text-[var(--color-error)] transition hover:opacity-80"
                    aria-label="Excluir etapa"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-[1.3fr,0.4fr]">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[var(--text-muted)]">Nome da etapa</label>
                  <input
                    value={followup.title}
                    onChange={(event) => updateFollowUp(followup.id, { title: event.target.value })}
                    className="w-full rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
                    placeholder="Ex.: Follow-up 1"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[var(--text-muted)]">Delay da etapa</label>
                  <input
                    type="number"
                    min={1}
                    max={43200}
                    value={followup.delay_minutes}
                    onChange={(event) =>
                      updateFollowUp(followup.id, { delay_minutes: Number(event.target.value || 0) })
                    }
                    className="w-full rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-1.5">
                <label className="text-xs font-medium text-[var(--text-muted)]">Mensagem do follow-up</label>
                <textarea
                  value={followup.instructions}
                  onChange={(event) =>
                    updateFollowUp(followup.id, { instructions: event.target.value })
                  }
                  rows={4}
                  className="w-full resize-none rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
                  placeholder="Olá, passando para saber se faz sentido continuar por aqui."
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FlowStatusButtons({
  value,
  onChange,
}: {
  value: FollowUpFlowStatus
  onChange: (value: FollowUpFlowStatus) => void
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {FLOW_STATUS_OPTIONS.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center gap-2 rounded-[14px] border px-4 py-3 text-sm font-semibold transition ${
              active
                ? 'border-transparent bg-[linear-gradient(135deg,#2563eb,#0891b2)] text-white shadow-[0_10px_24px_rgba(37,99,235,0.24)]'
                : 'border-[var(--border-medium)] bg-[var(--glass-light)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {option.icon}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function InstanceBindingSelector({
  instances,
  selectedIds,
  fallbackInstanceId,
  onChange,
}: {
  instances: Array<{
    id: string
    name: string
    phone_number?: string
    status: string
    remote_instance_id?: string | null
  }>
  selectedIds: string[]
  fallbackInstanceId?: string | null
  onChange: (value: string[]) => void
}) {
  function toggleInstance(instanceId: string) {
    if (selectedIds.includes(instanceId)) {
      onChange(selectedIds.filter((value) => value !== instanceId))
      return
    }
    onChange([...selectedIds, instanceId])
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Instâncias vinculadas</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Flow ativo só roda nas instâncias marcadas. Se nada estiver marcado, usa instância atual do workspace ao salvar.
          </p>
        </div>
        {fallbackInstanceId ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onChange(
                selectedIds.includes(fallbackInstanceId)
                  ? selectedIds
                  : [...selectedIds, fallbackInstanceId],
              )
            }
          >
            Usar instância atual
          </Button>
        ) : null}
      </div>

      {instances.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-5 text-sm text-[var(--text-muted)]">
          Nenhuma instância carregada. Sem instância vinculada, follow-up ativo não salva.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {instances.map((instance) => {
            const active = selectedIds.includes(instance.id)
            return (
              <button
                key={instance.id}
                type="button"
                onClick={() => toggleInstance(instance.id)}
                className={`rounded-[18px] border px-4 py-4 text-left transition ${
                  active
                    ? 'border-[var(--color-brand)] bg-[rgba(37,99,235,0.12)]'
                    : 'border-[var(--border-subtle)] bg-[var(--glass-light)]'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{instance.name}</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">
                      {instance.phone_number || 'Sem número'} · {instance.remote_instance_id || instance.id}
                    </p>
                  </div>
                  <Badge variant={active ? 'brand' : 'default'} size="sm">
                    {active ? 'Vinculada' : instance.status}
                  </Badge>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function RuleConfigForm({
  value,
  onChange,
}: {
  value: FollowUpRuleConfig
  onChange: (value: FollowUpRuleConfig) => void
}) {
  function patchRule(patch: Partial<FollowUpRuleConfig>) {
    onChange({ ...value, ...patch })
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
        Após lead não responder por
        <select
          value={value.leadWithoutResponseForMinutes}
          onChange={(event) => patchRule({ leadWithoutResponseForMinutes: Number(event.target.value) })}
          className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
        >
          {[5, 10, 15, 30, 60, 120].map((minutes) => (
            <option key={minutes} value={minutes}>
              {minutes} minutos
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
        Última mensagem
        <select
          value={value.lastMessageDirection}
          onChange={(event) =>
            patchRule({ lastMessageDirection: event.target.value as LastMessageDirection })
          }
          className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
        >
          <option value="outbound">Enviada pelo agente/sistema</option>
          <option value="inbound">Recebida do lead</option>
          <option value="any">Qualquer uma</option>
        </select>
      </label>

      <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
        Status da conversa
        <select
          value={value.conversationStatus}
          onChange={(event) =>
            patchRule({ conversationStatus: event.target.value as ConversationStatusFilter })
          }
          className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
        >
          <option value="open">Aberta</option>
          <option value="waiting">Aguardando</option>
          <option value="any">Qualquer status</option>
        </select>
      </label>

      <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
        Limite de contatos
        <input
          type="number"
          min={1}
          max={10000}
          value={value.contactLimit}
          onChange={(event) => patchRule({ contactLimit: Number(event.target.value || 0) })}
          className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
        />
      </label>

      <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
        Executar apenas em horário comercial?
        <select
          value={value.onlyBusinessHours ? 'yes' : 'no'}
          onChange={(event) => patchRule({ onlyBusinessHours: event.target.value === 'yes' })}
          className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
        >
          <option value="no">Não</option>
          <option value="yes">Sim</option>
        </select>
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
          Início
          <input
            type="time"
            value={value.businessHoursStart}
            disabled={!value.onlyBusinessHours}
            onChange={(event) => patchRule({ businessHoursStart: event.target.value })}
            className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)] disabled:opacity-50"
          />
        </label>
        <label className="grid gap-2 text-xs font-medium text-[var(--text-muted)]">
          Fim
          <input
            type="time"
            value={value.businessHoursEnd}
            disabled={!value.onlyBusinessHours}
            onChange={(event) => patchRule({ businessHoursEnd: event.target.value })}
            className="min-h-[44px] rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)] disabled:opacity-50"
          />
        </label>
      </div>
    </div>
  )
}

function WebhookConfigForm({
  value,
  onChange,
}: {
  value: FollowUpWebhookConfig
  onChange: (value: FollowUpWebhookConfig) => void
}) {
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  function patch(patchValue: Partial<FollowUpWebhookConfig>) {
    onChange({ ...value, ...patchValue })
  }

  function insertVariable(key: string) {
    const token = `{{${key}}}`
    const el = bodyRef.current
    const current = value.bodyTemplate ?? ''
    if (!el) {
      patch({ bodyTemplate: `${current}${token}` })
      return
    }
    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? start
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`
    patch({ bodyTemplate: next })
    requestAnimationFrame(() => {
      const caret = start + token.length
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Webhook do follow-up</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            A variável <code className="rounded bg-[var(--glass-light)] px-1">{'{{mensagem}}'}</code> é
            sempre a última mensagem recebida do lead. Para usar o texto configurado na etapa, use{' '}
            <code className="rounded bg-[var(--glass-light)] px-1">{'{{followupMessage}}'}</code>.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(event) => patch({ enabled: event.target.checked })}
          />
          Ativo
        </label>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--text-muted)]">URL do webhook</label>
        <input
          value={value.endpoint}
          onChange={(event) => patch({ endpoint: event.target.value })}
          placeholder={DEFAULT_FOLLOWUP_WEBHOOK_URL}
          className="w-full rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--text-muted)]">
          Corpo da requisição (JSON com variáveis)
        </label>
        <textarea
          ref={bodyRef}
          value={value.bodyTemplate}
          onChange={(event) => patch({ bodyTemplate: event.target.value })}
          rows={6}
          className="w-full resize-none rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 font-mono text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder={DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-[var(--text-muted)]">Método</span>
          <input
            value={value.method}
            readOnly
            className="w-full rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] opacity-80"
          />
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-[var(--text-muted)]">Estratégia de falha</span>
          <select
            value={value.failStrategy}
            onChange={(event) =>
              patch({ failStrategy: event.target.value === 'stop' ? 'stop' : 'continue' })
            }
            className="min-h-[44px] rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-3 text-sm text-[var(--text-primary)]"
          >
            <option value="continue">Continuar</option>
            <option value="stop">Parar fluxo</option>
          </select>
        </label>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--text-muted)]">
          Headers extras (uma "Chave: Valor" por linha)
        </label>
        <textarea
          value={typeof value.headers === 'string' ? value.headers : ''}
          onChange={(event) => patch({ headers: event.target.value })}
          rows={3}
          className="w-full resize-none rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 font-mono text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder={'Authorization: Bearer xxx\nX-Custom: valor'}
        />
      </div>

      <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
        <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--text-muted)]">
          Variáveis disponíveis
        </p>
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">
          Clique em uma variável para inseri-la no corpo da requisição.
        </p>
        <ul className="mt-3 space-y-2 text-xs text-[var(--text-secondary)]">
          {FOLLOWUP_WEBHOOK_VARIABLES.map((variable) => (
            <li key={variable.key} className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => insertVariable(variable.key)}
                title="Inserir no corpo"
                className="shrink-0 rounded bg-[var(--glass-medium)] px-2 py-0.5 text-[11px] text-[var(--text-primary)] transition hover:bg-[var(--color-brand-light)] hover:text-[var(--color-brand)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
              >
                {`{{${variable.key}}}`}
              </button>
              <span>{variable.description}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function FollowUpEditorModal({
  initialFlow,
  instances,
  fallbackInstanceId,
  onClose,
  onSave,
}: {
  initialFlow: AgentDefinition
  instances: Array<{
    id: string
    name: string
    phone_number?: string
    status: string
    remote_instance_id?: string | null
  }>
  fallbackInstanceId?: string | null
  onClose: () => void
  onSave: (flow: AgentDefinition) => Promise<void>
}) {
  const [form, setForm] = useState<AgentDefinition>(initialFlow)
  const [saving, setSaving] = useState(false)
  const [ruleConfig, setRuleConfig] = useState<FollowUpRuleConfig>(() => getFlowRuleConfig(initialFlow))
  const [webhookConfig, setWebhookConfig] = useState<FollowUpWebhookConfig>(() =>
    getFlowWebhookConfig(initialFlow),
  )
  const [instanceIds, setInstanceIds] = useState<string[]>(() => {
    const stored = getFlowInstanceIds(initialFlow)
    if (stored.length > 0) return stored
    return fallbackInstanceId ? [fallbackInstanceId] : []
  })

  const flowStatus = getFlowStatus(form)

  function updateStatus(status: FollowUpFlowStatus) {
    setForm((current) => ({
      ...current,
      enabled: status === 'active',
      metadata: {
        ...(current.metadata ?? {}),
        follow_up_status: status,
      },
    }))
  }

  async function handleSave() {
    const effectiveInstanceIds =
      instanceIds.length > 0
        ? instanceIds
        : fallbackInstanceId
          ? [fallbackInstanceId]
          : []

    try {
      validateFollowUpFlow(form, ruleConfig)
      if (getFlowStatus(form) === 'active' && effectiveInstanceIds.length === 0) {
        throw new Error('Para ativar o follow-up, vincule pelo menos uma instância.')
      }
      validateWebhookConfig(webhookConfig)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Configuração inválida.')
      return
    }

    if (webhookConfig.enabled) {
      try {
        const preview = await AgentsService.previewFollowup(
          ruleConfig as unknown as Record<string, unknown>,
        )
        if (preview.eligible === 0) {
          toast.warning(
            'Nenhuma conversa dentro das condições agora. O webhook não dispararia até surgir um lead elegível.',
          )
        } else if (preview.with_inbound === 0) {
          toast.warning(
            `${preview.eligible} conversa(s) dentro das condições, mas nenhuma tem mensagem do lead — a última mensagem ficaria vazia e o disparo seria pulado.`,
          )
        } else {
          toast.info(
            `${preview.with_inbound} de ${preview.eligible} conversa(s) dentro das condições têm mensagem do lead.`,
          )
        }
      } catch {
        // Checagem é informativa e não bloqueia o salvamento.
      }
    }

    setSaving(true)
    try {
      const normalizedName = form.name.trim()
      const normalizedWebhook: FollowUpWebhookConfig = {
        enabled: webhookConfig.enabled,
        endpoint: webhookConfig.endpoint.trim() || DEFAULT_FOLLOWUP_WEBHOOK_URL,
        method: 'POST',
        bodyTemplate:
          webhookConfig.bodyTemplate.trim() || DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE,
        headers: webhookConfig.headers,
        failStrategy: webhookConfig.failStrategy,
      }
      await onSave({
        ...form,
        name: normalizedName,
        supervisor_name: form.supervisor_name.trim() || normalizedName,
        persona_name: form.persona_name.trim() || 'Follow-up',
        system_prompt: buildRuleSummary(ruleConfig, normalizedWebhook),
        metadata: {
          ...(form.metadata ?? {}),
          follow_up_status: getFlowStatus(form),
          follow_up_rule_config: ruleConfig,
          follow_up_webhook_config: {
            ...normalizedWebhook,
            headers: parseHeadersText(
              typeof normalizedWebhook.headers === 'string' ? normalizedWebhook.headers : '',
            ),
          },
          follow_up_instance_ids: effectiveInstanceIds,
        },
      })
      toast.success('Fluxo de follow-up salvo.')
      onClose()
    } catch {
      toast.error('Nao foi possivel salvar o fluxo de follow-up.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-[var(--text-muted)]">Nome do fluxo</label>
          <input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            className="w-full rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            placeholder="Ex.: Recuperação de orçamento"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-[var(--text-muted)]">Nome interno</label>
          <input
            value={form.supervisor_name}
            onChange={(event) =>
              setForm((current) => ({ ...current, supervisor_name: event.target.value }))
            }
            className="w-full rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            placeholder="follow_up_1"
          />
        </div>
      </div>

      <section className="space-y-3 rounded-[22px] border border-[var(--border-subtle)] bg-[var(--glass-medium)] p-5">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Status do fluxo</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Apenas um estado por vez. `disabled` e `paused` impedem execução.
          </p>
        </div>
        <FlowStatusButtons value={flowStatus} onChange={updateStatus} />
      </section>

      <section className="space-y-4 rounded-[22px] border border-[var(--border-subtle)] bg-[var(--glass-medium)] p-5">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Regra if/else</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Estruture quando o follow-up pode rodar. Padrão recomendado aqui: última mensagem inbound do lead.
          </p>
        </div>

        <RuleConfigForm value={ruleConfig} onChange={setRuleConfig} />

        <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--text-muted)]">
            Preview operacional
          </p>
          <pre className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[var(--text-primary)]">
            {buildRuleSummary(ruleConfig, webhookConfig)}
          </pre>
        </div>
      </section>

      <section className="space-y-4 rounded-[22px] border border-[var(--border-subtle)] bg-[var(--glass-medium)] p-5">
        <InstanceBindingSelector
          instances={instances}
          selectedIds={instanceIds}
          fallbackInstanceId={fallbackInstanceId}
          onChange={setInstanceIds}
        />
      </section>

      <section className="space-y-4 rounded-[22px] border border-[var(--border-subtle)] bg-[var(--glass-medium)] p-5">
        <div className="flex items-center gap-2">
          <Webhook size={16} className="text-[var(--color-brand)]" />
          <p className="text-sm font-semibold text-[var(--text-primary)]">Disparo via webhook</p>
        </div>
        <WebhookConfigForm value={webhookConfig} onChange={setWebhookConfig} />
      </section>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--text-muted)]">
          Observações internas (não enviadas ao webhook)
        </label>
        <textarea
          value={form.knowledge_base}
          onChange={(event) =>
            setForm((current) => ({ ...current, knowledge_base: event.target.value }))
          }
          rows={4}
          className="w-full resize-none rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Contexto comercial usado nas retomadas, exceções e observações operacionais."
        />
      </div>

      <FollowUpSequenceEditor
        followups={form.followups}
        onChange={(followups) => setForm((current) => ({ ...current, followups }))}
      />

      <div className="sticky bottom-0 flex justify-end gap-3 border-t border-[var(--border-subtle)] bg-[var(--glass-strong)] px-1 pt-4 backdrop-blur-xl">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button icon={<Save size={14} />} loading={saving} onClick={() => handleSave().catch(() => undefined)}>
          Salvar follow-up
        </Button>
      </div>
    </div>
  )
}

export function FollowUpTab() {
  const instances = useInstancesStore((state) => state.instances)
  const selectedInstance = useInstancesStore((state) => state.selectedInstance)
  const fetchInstances = useInstancesStore((state) => state.fetchInstances)
  const agents = useAgentCatalogStore((state) => state.agents)
  const fetchAgents = useAgentCatalogStore((state) => state.fetchAgents)
  const createAgentRemote = useAgentCatalogStore((state) => state.createAgent)
  const updateAgentRemote = useAgentCatalogStore((state) => state.updateAgent)
  const deleteAgentRemote = useAgentCatalogStore((state) => state.deleteAgent)
  const [editingFlow, setEditingFlow] = useState<AgentDefinition | null>(null)
  const [flowToDelete, setFlowToDelete] = useState<AgentDefinition | null>(null)

  useEffect(() => {
    fetchAgents().catch(() => {
      toast.error('Nao foi possivel carregar os fluxos de follow-up.')
    })
    fetchInstances().catch(() => {
      toast.error('Nao foi possivel carregar as instâncias vinculáveis.')
    })
  }, [fetchAgents, fetchInstances])

  const bindingsCount = useMemo(() => {
    return new Map(agents.map((flow) => [flow.id, getFlowInstanceIds(flow).length]))
  }, [agents])

  const linkedInstancesTotal = useMemo(() => {
    const ids = new Set<string>()
    agents.forEach((flow) => {
      getFlowInstanceIds(flow).forEach((instanceId) => ids.add(instanceId))
    })
    return ids.size
  }, [agents])

  function openCreateFlow() {
    const nextFlow = createFlow(agents.length + 1)
    if (selectedInstance?.id) {
      nextFlow.metadata = {
        ...(nextFlow.metadata ?? {}),
        follow_up_instance_ids: [selectedInstance.id],
      }
    }
    setEditingFlow(nextFlow)
  }

  async function handleSaveFlow(flow: AgentDefinition) {
    const payload = {
      name: flow.name.trim(),
      supervisor_name: flow.supervisor_name.trim() || flow.name.trim(),
      persona_name: flow.persona_name.trim() || 'Follow-up',
      personality: flow.personality,
      system_prompt: flow.system_prompt,
      knowledge_base: flow.knowledge_base,
      knowledge_sources: sanitizeKnowledgeSources(flow.knowledge_sources),
      subagents: sanitizeSubagents(flow.subagents),
      followups: flow.followups,
      metadata: flow.metadata ?? {},
      enabled: getFlowStatus(flow) === 'active',
      memory_enabled: flow.memory_enabled,
      strict_logic_flow: flow.strict_logic_flow,
    }

    const existsInCatalog = agents.some((existing) => existing.id === flow.id)
    if (existsInCatalog) {
      await updateAgentRemote(flow.id, payload)
      return
    }

    await createAgentRemote(payload)
  }

  return (
    <div className="space-y-5 pb-6">
      <Card className="p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-4">
            <Badge variant="brand">Follow-up orientado por histórico sincronizado</Badge>
            <div className="space-y-2">
              <h2 className="text-[28px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                Follow-up
              </h2>
              <p className="max-w-3xl text-sm text-[var(--text-secondary)]">
                Centralize retomadas automáticas, valide contexto da conversa antes de agir e distribua
                sequências por tempo de espera, exceção e etapa do lead.
              </p>
            </div>
          </div>

          <Button icon={<Plus size={14} />} onClick={openCreateFlow}>
            Novo fluxo
          </Button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-4">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Fluxos</p>
            <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{agents.length}</p>
          </div>
          <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-4">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Etapas ativas</p>
            <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
              {agents.reduce(
                (total, agent) => total + agent.followups.filter((followup) => followup.active).length,
                0,
              )}
            </p>
          </div>
          <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-4">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Instâncias com vínculo</p>
            <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
              {linkedInstancesTotal}
            </p>
          </div>
          <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-4">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Proteção de sync</p>
            <p className="mt-2 text-sm font-semibold text-[var(--color-success)]">Ativa no backend</p>
          </div>
        </div>
      </Card>

      {agents.length === 0 ? (
        <div className="stub-panel">
          <MessageSquare size={30} className="text-[var(--color-brand)]" />
          <h3>Nenhum fluxo de follow-up criado ainda</h3>
          <p>
            Crie primeira sequência, defina atrasos por etapa e deixe proteção de sincronia bloquear
            disparos fora de hora.
          </p>
          <Button icon={<Plus size={14} />} onClick={openCreateFlow}>
            Criar follow-up
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {agents.map((flow) => {
            const linkedInstances = bindingsCount.get(flow.id) ?? 0
            const activeSteps = flow.followups.filter((followup) => followup.active).length
            const status = getFlowStatus(flow)
            const ruleConfig = getFlowRuleConfig(flow)
            const webhookConfig = getFlowWebhookConfig(flow)

            return (
              <Card key={flow.id} className="space-y-4 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-[var(--text-primary)]">{flow.name}</h3>
                      <Badge variant={getStatusBadgeVariant(status)} size="sm">
                        {getStatusLabel(status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {buildRuleSummary(ruleConfig, webhookConfig)}
                    </p>
                    <p className="text-xs font-medium text-[var(--text-muted)]">
                      Instâncias vinculadas: {linkedInstances}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setFlowToDelete(flow)}
                    className="text-[var(--color-error)] transition hover:opacity-80"
                    aria-label="Excluir fluxo de follow-up"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      <Clock3 size={14} className="text-[var(--color-brand)]" />
                      Primeira etapa
                    </div>
                    <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
                      {getFirstDelayLabel(flow.followups)}
                    </p>
                  </div>

                  <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      <GitBranch size={14} className="text-[var(--color-brand)]" />
                      Etapas ativas
                    </div>
                    <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
                      {activeSteps} de {flow.followups.length}
                    </p>
                  </div>

                  <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      <Link2 size={14} className="text-[var(--color-brand)]" />
                      Limite de contatos
                    </div>
                    <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
                      {ruleConfig.contactLimit}
                    </p>
                  </div>

                  <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      <ShieldCheck size={14} className="text-[var(--color-brand)]" />
                      Guarda de sync
                    </div>
                    <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
                      {flow.strict_logic_flow ? 'Bloqueio ativo' : 'Flexível'}
                    </p>
                  </div>
                </div>

                <Button variant="outline" fullWidth onClick={() => setEditingFlow(flow)}>
                  Configurar follow-up
                </Button>
              </Card>
            )
          })}
        </div>
      )}

      <Modal
        open={Boolean(editingFlow)}
        onClose={() => setEditingFlow(null)}
        title={editingFlow ? `Configurar follow-up — ${editingFlow.name}` : 'Configurar follow-up'}
        description="Edite status, regra operacional e sequência antes de liberar o fluxo."
        size="2xl"
      >
        {editingFlow ? (
          <FollowUpEditorModal
            initialFlow={editingFlow}
            instances={instances}
            fallbackInstanceId={selectedInstance?.id}
            onClose={() => setEditingFlow(null)}
            onSave={handleSaveFlow}
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(flowToDelete)}
        onClose={() => setFlowToDelete(null)}
        title="Excluir fluxo de follow-up"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            Deseja remover o fluxo <strong>{flowToDelete?.name}</strong>?
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setFlowToDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!flowToDelete) return
                try {
                  await deleteAgentRemote(flowToDelete.id)
                  toast.success('Fluxo removido.')
                } catch {
                  toast.error('Nao foi possivel remover o fluxo.')
                }
                setFlowToDelete(null)
              }}
            >
              Excluir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
